/**
 * Revenue Leak Intelligence Selectors — Single Source of Truth
 *
 * Composes existing selector layers to detect, quantify, and prioritize
 * revenue leaks across the platform. No duplicate calculations.
 */

import type { AppointmentResult } from "./appointmentResultTypes";
import type { Opportunity } from "./opportunityTypes";
import type { FollowUpTask } from "./opportunityTypes";
import type { Conversation, Message } from "./communicationTypes";
import type { Job } from "./types";

import { getRevenueLeaks, getDisplayPrice } from "./appointmentResultSelectors";
import { getAppointmentsWithoutOutcome } from "./appointmentSelectors";
import {
  getAllFollowUps,
  getOverdueFollowUps,
  type EnrichedFollowUp,
} from "./followUpSelectors";
import {
  getAllLeads,
  getLeadsNeedingResponse,
  getStaleLeads,
  type EnrichedLead,
} from "./leadSelectors";

/* ═══════ TYPES ═══════ */

export type LeakType =
  | "no_sale_no_followup"
  | "no_demo_no_reschedule"
  | "high_value_lost"
  | "stale_followups"
  | "uncontacted_leads"
  | "appointments_without_outcome";

export type UrgencyLevel = "low" | "medium" | "high";

export interface LeakCategory {
  type: LeakType;
  label: string;
  count: number;
  revenueAtRisk: number;
  urgency: UrgencyLevel;
  navigateTo: string;
  items: unknown[];
}

export interface RevenueLeakSummary {
  categories: LeakCategory[];
  totalRevenueAtRisk: number;
  totalLeakCount: number;
  highUrgencyCount: number;
  overallUrgency: UrgencyLevel;
}

/* ═══════ DEPS ═══════ */

export interface RevenueLeakDeps {
  appointmentResults: AppointmentResult[];
  salesOpportunities: Opportunity[];
  followUpTasks: FollowUpTask[];
  conversations: Conversation[];
  messages: Message[];
  /** Optional — when supplied, SOLD revenue resolves canonically. */
  jobs?: Job[];
}

/* ═══════ URGENCY HELPERS ═══════ */

function deriveUrgency(count: number, revenue: number): UrgencyLevel {
  if (count === 0) return "low";
  if (revenue >= 50000 || count >= 5) return "high";
  if (revenue >= 15000 || count >= 2) return "medium";
  return "low";
}

/* ═══════ CORE DETECTION ═══════ */

export function getRevenueLeakSummary(deps: RevenueLeakDeps): RevenueLeakSummary {
  const {
    appointmentResults,
    salesOpportunities,
    followUpTasks,
    conversations,
    messages,
    jobs,
  } = deps;

  // 1. Result-based leaks (from appointmentResultSelectors)
  const followUpOppIds = new Set(followUpTasks.map((t) => t.opportunityId));
  const resultLeaks = getRevenueLeaks(appointmentResults, followUpOppIds, jobs);

  const noSaleRevenue = resultLeaks.noSaleNoFollowUp.reduce(
    (s, r) => s + getDisplayPrice(r, jobs),
    0,
  );
  const highValueRevenue = resultLeaks.highValueLost.reduce(
    (s, r) => s + getDisplayPrice(r, jobs),
    0,
  );

  // 2. Follow-up leaks (from followUpSelectors)
  const allFollowUps = getAllFollowUps({
    followUpTasks,
    salesOpportunities,
    conversations,
    messages,
  });
  const overdueFollowUps = getOverdueFollowUps(allFollowUps);
  const staleFollowUpRevenue = overdueFollowUps
    .filter((f) => f.hoursOverdue > 48)
    .reduce((s, f) => s + f.dealValue, 0);
  const staleItems = overdueFollowUps.filter((f) => f.hoursOverdue > 48);

  // 3. Lead leaks (from leadSelectors)
  const allLeads = getAllLeads({ salesOpportunities, conversations, messages });
  const uncontacted = getLeadsNeedingResponse(allLeads);
  const uncontactedRevenue = uncontacted.reduce(
    (s, l) => s + (l.lead.totalEstimate || 0),
    0,
  );

  // 4. Appointments without outcome (from appointmentSelectors)
  const appointmentsWithoutOutcome = getAppointmentsWithoutOutcome({
    salesOpportunities,
    appointmentResults,
  });
  const missingOutcomeRevenue = appointmentsWithoutOutcome.reduce(
    (s, o) => s + (o.totalEstimate || 0),
    0,
  );

  // Build categories sorted by revenue then count
  const categories: LeakCategory[] = ([
    {
      type: "no_sale_no_followup" as LeakType,
      label: "No Sale without Follow-Up",
      count: resultLeaks.noSaleNoFollowUp.length,
      revenueAtRisk: noSaleRevenue,
      urgency: deriveUrgency(resultLeaks.noSaleNoFollowUp.length, noSaleRevenue),
      navigateTo: "/follow-ups",
      items: resultLeaks.noSaleNoFollowUp,
    },
    {
      type: "high_value_lost" as LeakType,
      label: "High Value Lost Deals",
      count: resultLeaks.highValueLost.length,
      revenueAtRisk: highValueRevenue,
      urgency: deriveUrgency(resultLeaks.highValueLost.length, highValueRevenue),
      navigateTo: "/appointment-results",
      items: resultLeaks.highValueLost,
    },
    {
      type: "stale_followups" as LeakType,
      label: "Stale Follow-Ups (48h+)",
      count: staleItems.length,
      revenueAtRisk: staleFollowUpRevenue,
      urgency: deriveUrgency(staleItems.length, staleFollowUpRevenue),
      navigateTo: "/follow-ups",
      items: staleItems,
    },
    {
      type: "appointments_without_outcome" as LeakType,
      label: "Appointments Missing Outcome",
      count: appointmentsWithoutOutcome.length,
      revenueAtRisk: missingOutcomeRevenue,
      urgency: deriveUrgency(appointmentsWithoutOutcome.length, missingOutcomeRevenue),
      navigateTo: "/opportunities",
      items: appointmentsWithoutOutcome,
    },
    {
      type: "uncontacted_leads" as LeakType,
      label: "Uncontacted Leads",
      count: uncontacted.length,
      revenueAtRisk: uncontactedRevenue,
      urgency: deriveUrgency(uncontacted.length, uncontactedRevenue),
      navigateTo: "/pipeline",
      items: uncontacted,
    },
    {
      type: "no_demo_no_reschedule" as LeakType,
      label: "No Demo without Reschedule",
      count: resultLeaks.noDemoNoReschedule.length,
      revenueAtRisk: 0,
      urgency: deriveUrgency(resultLeaks.noDemoNoReschedule.length, 0),
      navigateTo: "/appointment-results",
      items: resultLeaks.noDemoNoReschedule,
    },
  ] as LeakCategory[]).sort((a, b) => b.revenueAtRisk - a.revenueAtRisk || b.count - a.count);

  const totalRevenueAtRisk = categories.reduce((s, c) => s + c.revenueAtRisk, 0);
  const totalLeakCount = categories.reduce((s, c) => s + c.count, 0);
  const highUrgencyCount = categories.filter((c) => c.urgency === "high").length;

  const overallUrgency: UrgencyLevel =
    highUrgencyCount >= 2 ? "high" : highUrgencyCount >= 1 ? "medium" : totalLeakCount > 0 ? "low" : "low";

  return {
    categories,
    totalRevenueAtRisk,
    totalLeakCount,
    highUrgencyCount,
    overallUrgency,
  };
}
